/**
 * ImageSelectQuiz Adult Renderer
 * Thin rendering component that delegates logic to useImageSelectQuiz hook.
 */

import { cn } from '../../../lib/utils'
import { QuizResultFeedback, SubmitButton, QuizSolveDescription, QuizHeader } from '../shared'
import { formatText } from '../../../utils/quiz-parser'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import { useImageSelectQuiz } from '../hooks'
import type { ImageSelectQuizData } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface ImageSelectQuizProps {
  quizId: string | number
  quiz: ImageSelectQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[] // 제출된 답안 (부모에서 전달, 채점 후에도 유지)
  submitLabel?: string // 제출 버튼 라벨 (default: '정답 확인')
  hideSubmitButton?: boolean // 제출 버튼 숨김 (체크업 등에서 외부 버튼 사용 시)
  onAnswerChange?: (answers: string[]) => void // 답변 변경 시 콜백
  disableSound?: boolean // 사운드 비활성화 (리뷰 모드 등)
  difficultyLevel?: number | null // 난이도 (1-5)
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function ImageSelectQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: ImageSelectQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    selectedKeys,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggle,
    handleSubmit,
  } = useImageSelectQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.imageSelect.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={undefined}
        imageMobile={undefined}
        desmos={quiz.desmos}
        vars={quiz.vars}
        problem={quiz.problem && (
          <div className="text-xl leading-relaxed text-gray-900">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      {/* 이미지 + 오버레이 영역 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center justify-center"
      >
        <div className="relative inline-block">
          {/* 베이스 이미지 */}
          {quiz.image && (
            <img
              src={quiz.image.signedUrl || quiz.image.url}
              alt="quiz"
              className="max-w-full h-auto"
            />
          )}

          {/* 오버레이 사각형 영역 */}
          {quiz.choices.map((rect) => {
            const isSelected = selectedKeys.has(rect.key)
            const isCorrectAnswer = solveResult?.correctAnswers?.includes(rect.key)

            // 값 클램핑
            const x = Math.max(0, rect.x)
            const y = Math.max(0, rect.y)
            const width = rect.x + rect.width > 100 ? 100 - rect.x : rect.width
            const height = rect.y + rect.height > 100 ? 100 - rect.y : rect.height

            const showAsCorrect = isSolved && shouldShowCorrectAnswers && isCorrectAnswer
            const showAsMyCorrect = isSolved && !shouldShowCorrectAnswers && isSelected && isCorrectAnswer
            const showAsWrong = isSolved && !shouldShowCorrectAnswers && isSelected && !isCorrectAnswer

            return (
              <button
                key={rect.key}
                onClick={() => handleToggle(rect.key)}
                disabled={isSolved || isViewSolved}
                className={cn(
                  'group absolute cursor-pointer border-4 border-transparent rounded-xl transition-all',
                  // 호버 (풀이 전, 미선택): 외곽 흰색 테두리
                  !isSolved && !isViewSolved && !isSelected && 'hover:border-white hover:shadow-lg',
                  // 선택 (풀이 전): 외곽 흰색 테두리
                  !isSolved && isSelected && 'border-white shadow-lg',
                  // 정답/오답 (풀이 후): 외곽 테두리 없음 (transparent 유지)
                  // 비활성화
                  (isSolved || isViewSolved) && 'pointer-events-none'
                )}
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: `${width}%`,
                  height: `${height}%`,
                }}
              >
                {/* 내부 오버레이 - 선택/정답/오답 상태 표시 */}
                <div className={cn(
                  'absolute inset-0 rounded-lg border-2 transition-all',
                  // 풀이 전 - 미선택: 대시 테두리, 호버 시에만 표시 (opacity)
                  !isSolved && !isSelected && 'border-dashed border-[#55C3FF] opacity-0 group-hover:opacity-100',
                  // 풀이 전 - 선택: 실선 테두리 + 배경
                  !isSolved && isSelected && 'border-solid border-[#55C3FF] bg-[#55C3FF]/20 opacity-100',
                  // 정답 (내 정답 또는 정답보기)
                  (showAsCorrect || showAsMyCorrect) && 'border-solid border-[#3BD0AE] bg-[#3BD0AE]/20 opacity-100',
                  // 오답
                  showAsWrong && 'border-solid border-[#FF675E] bg-[#FF675E]/20 opacity-100',
                  // 정답보기 모드 - 정답 영역 강조
                  shouldShowCorrectAnswers && isCorrectAnswer && 'border-solid border-[#3BD0AE] bg-[#3BD0AE]/20 opacity-100'
                )} />
              </button>
            )
          })}
        </div>
      </motion.div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? (selectedKeys.size === 0
            ? t('types.imageSelect.selectArea')
            : t('common.checkAnswer'))}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 해설 */}
      {isSolved && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult?.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
